import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DisplayValue, DisplayValueKind } from '@complan/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TextInput } from '@/features/initiative/intake/fields';
import { FindingsAlert } from './FindingsAlert';
import { useStatusFinality, useUpdateStatusFinality } from './admin-data';
import { findingsFromError } from './admin-findings';

/**
 * The Initiative status display editor (task 23.6, R16.10).
 *
 * The Initiative status set is fixed. R16.10 permits exactly one kind of change
 * to it — the display label and the display order of each value — and this
 * section is that surface. There is deliberately **no** create, delete or
 * deactivate control: the value column is read-only, and only the label and
 * order are editable. The server rejects any create or delete of a member; the
 * absence of the control here is the client half of the same rule.
 */
export function StatusFinalitySection(): React.JSX.Element {
  const { t } = useTranslation();
  // The Initiative status set is the only fixed display set that remains.
  const kind: DisplayValueKind = 'status';
  const listQuery = useStatusFinality(kind);
  const items = listQuery.data?.items ?? [];

  return (
    <Card className="gap-4 p-[14px]">
      <div>
        <p className="text-[13px] font-semibold text-txt">{t('admin.statusFinality.heading')}</p>
        <p className="text-muted-foreground mt-1 text-[11px]">{t('admin.statusFinality.hint')}</p>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10.5px] uppercase">{t('admin.statusFinality.table.colValue')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.statusFinality.table.colLabel')}</TableHead>
            <TableHead className="text-right text-[10.5px] uppercase">{t('admin.statusFinality.table.colOrder')}</TableHead>
            <TableHead className="text-right">
              <span className="sr-only">{t('admin.statusFinality.table.colActions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {listQuery.isError ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {t('admin.common.loadError')}
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {listQuery.isLoading ? t('admin.common.loading') : t('admin.statusFinality.table.empty')}
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => <DisplayValueRow key={item.value} kind={kind} item={item} />)
          )}
        </TableBody>
      </Table>
    </Card>
  );
}

/** One fixed-value row: the value is read-only; the label and order are editable (R16.10). */
function DisplayValueRow({
  kind,
  item,
}: {
  readonly kind: DisplayValueKind;
  readonly item: DisplayValue;
}): React.JSX.Element {
  const { t } = useTranslation();
  const updateMutation = useUpdateStatusFinality(kind);
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(item.displayLabel);
  const [order, setOrder] = useState(String(item.displayOrder));

  const findings = findingsFromError(updateMutation.error);

  const startEdit = (): void => {
    updateMutation.reset();
    setLabel(item.displayLabel);
    setOrder(String(item.displayOrder));
    setEditing(true);
  };

  const save = (): void => {
    updateMutation.mutate(
      { value: item.value, update: { displayLabel: label, displayOrder: Number(order) || 0 } },
      { onSuccess: () => setEditing(false) },
    );
  };

  if (!editing) {
    return (
      <TableRow>
        <TableCell className="font-mono text-[12px] text-txt">{item.value}</TableCell>
        <TableCell className="text-[12.3px] font-semibold text-txt">{item.displayLabel}</TableCell>
        <TableCell className="text-right text-[12.3px] tabular-nums">{item.displayOrder}</TableCell>
        <TableCell className="text-right">
          <Button type="button" size="xs" variant="outline" onClick={startEdit}>
            {t('admin.common.edit')}
          </Button>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className="font-mono text-[12px] text-txt">{item.value}</TableCell>
      <TableCell>
        <TextInput
          aria-label={t('admin.statusFinality.editor.displayLabel')}
          value={label}
          maxLength={100}
          onChange={(event) => setLabel(event.target.value)}
        />
        <FindingsAlert findings={findings} />
      </TableCell>
      <TableCell className="text-right">
        <TextInput
          aria-label={t('admin.statusFinality.editor.displayOrder')}
          type="number"
          min={0}
          className="w-20 text-right"
          value={order}
          onChange={(event) => setOrder(event.target.value)}
        />
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button type="button" size="xs" variant="outline" onClick={() => setEditing(false)}>
            {t('admin.common.cancel')}
          </Button>
          <Button type="button" size="xs" onClick={save} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? t('admin.common.saving') : t('admin.common.save')}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
